import React, { useState, useEffect } from "react";
import { db } from "../config/firebaseConfig";
import {
  collection,
  addDoc,
  getDocs,
  Timestamp,
  query,
  orderBy,
  updateDoc,
  doc,
  deleteDoc,
  where,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import NavBar from "../components/NavBar";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import styles from "../styling/threadsPage.module.css";
import { Link } from "react-router-dom";
import Select from "react-select";

function ThreadsPage() {
  const [threads, setThreads] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [sortBy, setSortBy] = useState("newest");
  const [selectedTag, setSelectedTag] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const { currentUser } = getAuth();

  const [formData, setFormData] = useState({
    title: "",
    content: "",
    tags: [],
    taggedUsers: [],
    pinned: false,
  });

  const [commentModal, setCommentModal] = useState({
    open: false,
    thread: null,
  });
  const [newComment, setNewComment] = useState("");

  const [allUsers, setAllUsers] = useState([]);

  const [editModal, setEditModal] = useState({ open: false, thread: null });
  const [editFormData, setEditFormData] = useState({
    title: "",
    content: "",
    tags: [],
    taggedUsers: [],
  });

  // Available tags for threads
  const availableTags = [
    "General",
    "Fundraising",
    "Legal",
    "AMA",
    "Technical",
    "Design",
    "Marketing",
    "Help Wanted",
    "Announcement",
  ];

  const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3000';

  useEffect(() => {
    fetchThreads();
    const fetchUsers = async () => {
      const usersSnapshot = await getDocs(collection(db, "adonis_users"));
      setAllUsers(
        usersSnapshot.docs.map((doc) => ({
          value: doc.id,
          label:
            doc.data().formData?.name || doc.data().name || doc.data().email,
          photoURL: doc.data().photoURL,
        }))
      );
    };
    fetchUsers();
  }, []);

  const fetchThreads = async () => {
    try {
      const threadsQuery = query(
        collection(db, "threads"),
        orderBy("createdAt", "desc")
      );
      const querySnapshot = await getDocs(threadsQuery);
      const threadsData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
      }));
      setThreads(threadsData);
    } catch (error) {
      console.error("Error fetching threads:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      alert("Please sign in to post");
      return;
    }

    // Fetch user's profile data to get their name
    const userProfileQuery = query(
      collection(db, "adonis_users"),
      where("formData.email", "==", user.email)
    );
    const userProfileSnapshot = await getDocs(userProfileQuery);
    const userProfile = userProfileSnapshot.docs[0]?.data();
    const userName = userProfile?.formData?.name || user.email;

    const threadData = {
      ...formData,
      taggedUsers: formData.taggedUsers.map((u) => ({
        id: u.value,
        name: u.label,
      })),
      createdBy: user.uid,
      createdAt: Timestamp.now(),
      authorName: userName,
      authorPhotoURL: user.photoURL,
      comments: [],
      likes: 0,
    };

    try {
      // Create the thread
      const threadRef = await addDoc(collection(db, "threads"), threadData);

      // If this is an announcement, send emails
      if (formData.tags.includes("Announcement")) {
        try {
          await fetch('/api/send-announcement-email', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              title: formData.title,
              content: formData.content,
              authorName: userName,
              authorEmail: user.email,
              threadId: threadRef.id,
              baseUrl: baseUrl
            }),
          });
        } catch (emailError) {
          console.error('Error sending announcement emails:', emailError);
        }
      }

      setShowForm(false);
      setFormData({
        title: "",
        content: "",
        tags: [],
        taggedUsers: [],
        pinned: false,
      });
      fetchThreads();
    } catch (error) {
      console.error("Error creating thread:", error);
      alert("Error creating thread");
    }
  };

  const getFilteredThreads = () => {
    let filtered = threads;

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(
        (thread) =>
          thread.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          thread.content.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Filter by tag
    if (selectedTag !== "all") {
      filtered = filtered.filter((thread) => thread.tags.includes(selectedTag));
    }

    // Split into pinned and unpinned threads
    const pinnedThreads = filtered.filter((thread) => thread.pinned);
    const unpinnedThreads = filtered.filter((thread) => !thread.pinned);

    // Sort unpinned threads based on selected sort option
    let sortedUnpinned;
    switch (sortBy) {
      case "newest":
        sortedUnpinned = [...unpinnedThreads].sort(
          (a, b) => b.createdAt - a.createdAt
        );
        break;
      case "popular":
        sortedUnpinned = [...unpinnedThreads].sort(
          (a, b) => (b.likedBy?.length || 0) - (a.likedBy?.length || 0)
        );
        break;
      default:
        sortedUnpinned = unpinnedThreads;
    }

    // Sort pinned threads by creation date (newest first)
    const sortedPinned = [...pinnedThreads].sort(
      (a, b) => b.createdAt - a.createdAt
    );

    // Combine pinned and unpinned threads
    return [...sortedPinned, ...sortedUnpinned];
  };

  // Like handler (toggle like/unlike)
  const handleLike = async (thread) => {
    if (!currentUser) {
      alert("Please sign in to like posts");
      return;
    }
    try {
      const threadRef = doc(db, "threads", thread.id);
      const likedBy = thread.likedBy || [];
      let updatedLikedBy;
      if (likedBy.includes(currentUser.uid)) {
        // Unlike
        updatedLikedBy = likedBy.filter((uid) => uid !== currentUser.uid);
      } else {
        // Like
        updatedLikedBy = [...likedBy, currentUser.uid];
      }
      await updateDoc(threadRef, { likedBy: updatedLikedBy });
      fetchThreads();
    } catch (error) {
      console.error("Error updating like:", error);
    }
  };

  // Comment submit handler
  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    if (!currentUser) {
      alert("Please sign in to comment");
      return;
    }
    if (!newComment.trim()) return;

    try {
      // Fetch user's profile data to get their name
      const userProfileQuery = query(
        collection(db, "adonis_users"),
        where("formData.email", "==", currentUser.email)
      );
      const userProfileSnapshot = await getDocs(userProfileQuery);
      const userProfile = userProfileSnapshot.docs[0]?.data();
      const userName = userProfile?.formData?.name || currentUser.email;

      const threadRef = doc(db, "threads", commentModal.thread.id);
      const updatedComments = [
        ...(commentModal.thread.comments || []),
        {
          text: newComment,
          author: userName,
          authorPhotoURL: currentUser.photoURL,
          createdAt: Timestamp.now(),
        },
      ];
      await updateDoc(threadRef, { comments: updatedComments });
      setNewComment("");
      setCommentModal({
        ...commentModal,
        thread: { ...commentModal.thread, comments: updatedComments },
      });
      fetchThreads();
    } catch (error) {
      console.error("Error adding comment:", error);
    }
  };

  const handleDelete = async (threadId) => {
    if (!currentUser) {
      alert("Please sign in to delete posts");
      return;
    }

    const threadRef = doc(db, "threads", threadId);

    try {
      await deleteDoc(threadRef);
      fetchThreads(); // Refresh the threads after deletion
    } catch (error) {
      console.error("Error deleting thread:", error);
      alert("Error deleting thread");
    }
  };

  const handlePin = async (thread) => {
    if (!currentUser) {
      alert("Please sign in to pin posts");
      return;
    }

    // Check if user is authorized to pin
    const authorizedUsers = [
      "Vll2BM8eWrWyHl2DO47cDxh37Q42",
      "e0ho4Cxz22fS0FGxMAfsUjYhSgd2",
    ];
    if (!authorizedUsers.includes(currentUser.uid)) {
      alert("You are not authorized to pin posts");
      return;
    }

    try {
      const threadRef = doc(db, "threads", thread.id);
      await updateDoc(threadRef, { pinned: !thread.pinned });
      fetchThreads();
    } catch (error) {
      console.error("Error updating pin status:", error);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editModal.thread) return;
    try {
      const threadRef = doc(db, "threads", editModal.thread.id);
      await updateDoc(threadRef, {
        title: editFormData.title,
        content: editFormData.content,
        tags: editFormData.tags,
        taggedUsers: editFormData.taggedUsers.map((u) => ({
          id: u.value,
          name: u.label,
        })),
      });
      setEditModal({ open: false, thread: null });
      fetchThreads();
    } catch (error) {
      console.error("Error updating thread:", error);
      alert("Error updating thread");
    }
  };

  return (
    <>
      <NavBar />
      <div className={styles["threads-page"]}>
        <div className={styles["threads-header"]}>
          <div className={styles["header-left"]}>
            <h1>Community Threads</h1>
            <div className={styles["tag-filters"]}>
              <button
                className={`${styles["tag-btn"]} ${selectedTag === "all" ? styles["active"] : ""
                  }`}
                onClick={() => setSelectedTag("all")}
              >
                All
              </button>
              {availableTags.map((tag) => (
                <button
                  key={tag}
                  className={`${styles["tag-btn"]} ${selectedTag === tag ? styles["active"] : ""
                    }`}
                  onClick={() => setSelectedTag(tag)}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>

          <div className={styles["header-right"]}>
            <div className={styles["sort-controls"]}>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className={styles["sort-select"]}
              >
                <option value="newest">Newest First</option>
                <option value="popular">Most Popular</option>
                <option value="pinned">Pinned First</option>
              </select>
            </div>
            {currentUser && (
              <button
                onClick={() => setShowForm(true)}
                className={styles["create-btn"]}
              >
                Create Thread
              </button>
            )}
          </div>
        </div>

        {showForm && (
          <div className={styles["modal-overlay"]}>
            <div className={styles["modal-content"]}>
              <h2>Create New Thread</h2>
              <form onSubmit={handleSubmit}>
                <div className={styles["form-group"]}>
                  <label>Title</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) =>
                      setFormData({ ...formData, title: e.target.value })
                    }
                    placeholder="What's on your mind?"
                  />
                </div>

                <div className={styles["form-group"]}>
                  <label>Content*</label>
                  <ReactQuill
                    value={formData.content}
                    onChange={(value) =>
                      setFormData({ ...formData, content: value })
                    }
                    modules={{
                      toolbar: [
                        ["bold", "italic", "underline"],
                        [{ list: "bullet" }],
                        ["link"],
                        ["code-block"],
                      ],
                    }}
                  />
                </div>

                <div className={styles["form-group"]}>
                  <label>Tags</label>
                  <div className={styles["tags-selection"]}>
                    {availableTags.map((tag) => (
                      <label key={tag} className={styles["tag-checkbox"]}>
                        <input
                          type="checkbox"
                          checked={formData.tags.includes(tag)}
                          onChange={(e) => {
                            const updatedTags = e.target.checked
                              ? [...formData.tags, tag]
                              : formData.tags.filter((t) => t !== tag);
                            setFormData({ ...formData, tags: updatedTags });
                          }}
                        />
                        {tag}
                      </label>
                    ))}
                  </div>
                </div>

                <div className={styles["form-group"]}>
                  <label>Tag People</label>
                  <Select
                    isMulti
                    options={allUsers}
                    value={formData.taggedUsers}
                    onChange={(selected) =>
                      setFormData({ ...formData, taggedUsers: selected })
                    }
                    onMenuOpen={() => {
                      setTimeout(() => {
                        const modalContent = document.querySelector(
                          `.${styles["modal-content"]}`
                        );
                        if (modalContent) {
                          modalContent.scrollTo({
                            top: modalContent.scrollHeight,
                            behavior: "smooth",
                          });
                        }
                      }, 100);
                    }}
                    placeholder="Type a name to tag..."
                    classNamePrefix="react-select"
                  />
                </div>

                <div className={styles["modal-actions"]}>
                  <button type="button" onClick={() => setShowForm(false)}>
                    Cancel
                  </button>
                  <button type="submit">Create Thread</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Comments Modal */}
        {commentModal.open && (
          <div className={styles["modal-overlay"]}>
            <div className={styles["modal-content"]}>
              <h2>Comments</h2>
              <div
                style={{ maxHeight: 300, overflowY: "auto", marginBottom: 16 }}
              >
                {(commentModal.thread.comments || []).length === 0 && (
                  <div>No comments yet.</div>
                )}
                {(commentModal.thread.comments || []).map((comment, idx) => (
                  <div key={idx} className={styles["comment-item"]}>
                    <div className={styles["author-row"]}>
                      {/* <div className={styles["author-avatar"]}>
                        {comment.authorPhotoURL ? (
                          <img
                            src={comment.authorPhotoURL}
                            alt={comment.author}
                          />
                        ) : (
                          comment.author?.charAt(0)
                        )}
                      </div> */}
                      <div className={styles["author-details"]}>
                        <span className={styles["author-name"]}>
                          <Link
                            to={`/profile/${comment.authorId}`}
                            className={styles["author-link"]}
                          >
                            {comment.author && comment.author.includes("@")
                              ? "Anonymous"
                              : comment.author}
                          </Link>
                        </span>
                        <span className={styles["comment-date"]}>
                          {formatDate(
                            comment.createdAt?.toDate
                              ? comment.createdAt.toDate()
                              : comment.createdAt
                          )}
                        </span>
                      </div>
                    </div>
                    <div className={styles["comment-text"]}>{comment.text}</div>
                  </div>
                ))}
              </div>
              <form onSubmit={handleCommentSubmit}>
                <div className={styles["form-group"]}>
                  <label>Add a comment</label>
                  <input
                    type="text"
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Write your comment..."
                    required
                  />
                </div>
                <div className={styles["modal-actions"]}>
                  <button
                    type="button"
                    onClick={() =>
                      setCommentModal({ open: false, thread: null })
                    }
                  >
                    Close
                  </button>
                  <button type="submit">Post Comment</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {editModal.open && (
          <div className={styles["modal-overlay"]}>
            <div className={styles["modal-content"]}>
              <h2>Edit Thread</h2>
              <form onSubmit={handleEditSubmit}>
                <div className={styles["form-group"]}>
                  <label>Title</label>
                  <input
                    type="text"
                    value={editFormData.title}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        title: e.target.value,
                      })
                    }
                    placeholder="What's on your mind?"
                  />
                </div>
                <div className={styles["form-group"]}>
                  <label>Content*</label>
                  <ReactQuill
                    value={editFormData.content}
                    onChange={(value) =>
                      setEditFormData({ ...editFormData, content: value })
                    }
                    modules={{
                      toolbar: [
                        ["bold", "italic", "underline"],
                        [{ list: "bullet" }],
                        ["link"],
                        ["code-block"],
                      ],
                    }}
                  />
                </div>
                <div className={styles["form-group"]}>
                  <label>Tags</label>
                  <div className={styles["tags-selection"]}>
                    {availableTags.map((tag) => (
                      <label key={tag} className={styles["tag-checkbox"]}>
                        <input
                          type="checkbox"
                          checked={editFormData.tags.includes(tag)}
                          onChange={(e) => {
                            const updatedTags = e.target.checked
                              ? [...editFormData.tags, tag]
                              : editFormData.tags.filter((t) => t !== tag);
                            setEditFormData({
                              ...editFormData,
                              tags: updatedTags,
                            });
                          }}
                        />
                        {tag}
                      </label>
                    ))}
                  </div>
                </div>
                <div className={styles["form-group"]}>
                  <label>Tag People</label>
                  <Select
                    isMulti
                    options={allUsers}
                    value={editFormData.taggedUsers}
                    onChange={(selected) =>
                      setEditFormData({
                        ...editFormData,
                        taggedUsers: selected,
                      })
                    }
                    placeholder="Type a name to tag..."
                    classNamePrefix="react-select"
                  />
                </div>
                <div className={styles["modal-actions"]}>
                  <button
                    type="button"
                    onClick={() => setEditModal({ open: false, thread: null })}
                  >
                    Cancel
                  </button>
                  <button type="submit">Save Changes</button>
                </div>
              </form>
            </div>
          </div>
        )}

        <div className={styles["threads-container"]}>
          <div className={styles["search-bar"]}>
            <input
              type="text"
              placeholder="Search threads..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {getFilteredThreads().map((thread) => {
            const likedBy = thread.likedBy || [];
            const isLiked = currentUser && likedBy.includes(currentUser.uid);
            const isAuthor =
              currentUser && thread.createdBy === currentUser.uid;

            return (
              <div key={thread.id} className={styles["thread-card"]}>
                {thread.pinned && (
                  <div className={styles["pinned-badge"]}>📌 Pinned</div>
                )}
                <div className={styles["thread-header"]}>
                  <div className={styles["author-info"]}>
                    <div className={styles["author-avatar"]}>
                      {thread.authorPhotoURL ? (
                        <img
                          src={thread.authorPhotoURL}
                          alt={thread.authorName}
                        />
                      ) : (
                        thread.authorName.charAt(0)
                      )}
                    </div>
                    <div>
                      {thread.title && thread.title.trim() !== "" && (
                        <h3>{thread.title}</h3>
                      )}
                      <span className={styles["author-name"]}>
                        <Link
                          to={`/profile/${thread.createdBy}`}
                          className={styles["author-link"]}
                        >
                          {thread.authorName}
                        </Link>
                      </span>{" "}
                      <span className={styles["post-date"]}>
                        • {formatDate(thread.createdAt)}
                      </span>
                    </div>
                  </div>
                  <div className={styles["thread-tags"]}>
                    {thread.tags.map((tag) => (
                      <span key={tag} className={styles["tag"]}>
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>

                <div
                  className={styles["thread-content"]}
                  dangerouslySetInnerHTML={{ __html: thread.content }}
                />

                {thread.taggedUsers && thread.taggedUsers.length > 0 && (
                  <div className={styles["tagged-users"]}>
                    with{" "}
                    {thread.taggedUsers.map((u, idx) => (
                      <Link
                        key={u.id}
                        to={`/profile/${u.id}`}
                        className={styles["tagged-user"]}
                      >
                        @{u.name}
                        {idx < thread.taggedUsers.length - 1 ? ", " : ""}
                      </Link>
                    ))}
                  </div>
                )}

                <div className={styles["thread-footer"]}>
                  <button
                    className={styles["like-btn"]}
                    onClick={() => handleLike(thread)}
                    style={{
                      fontWeight: isLiked ? "bold" : "normal",
                      color: isLiked ? "#2563eb" : undefined,
                    }}
                    disabled={!currentUser}
                    title={
                      currentUser
                        ? isLiked
                          ? "Unlike"
                          : "Like"
                        : "Sign in to like"
                    }
                  >
                    {isLiked ? "Upvotes" : "Upvotes"} {likedBy.length}
                  </button>
                  <button
                    className={styles["comment-btn"]}
                    onClick={() => setCommentModal({ open: true, thread })}
                  >
                    Comment {thread.comments?.length || 0}
                  </button>
                  {isAuthor && (
                    <>
                      <button
                        className={styles["edit-btn"]}
                        onClick={() => {
                          setEditFormData({
                            title: thread.title || "",
                            content: thread.content || "",
                            tags: thread.tags || [],
                            taggedUsers: thread.taggedUsers
                              ? thread.taggedUsers.map((u) => ({
                                value: u.id,
                                label: u.name,
                              }))
                              : [],
                          });
                          setEditModal({ open: true, thread });
                        }}
                      >
                        Edit
                      </button>
                      <button
                        className={styles["delete-btn"]}
                        onClick={() => handleDelete(thread.id)}
                      >
                        Delete
                      </button>
                    </>
                  )}
                  {currentUser &&
                    [
                      "Vll2BM8eWrWyHl2DO47cDxh37Q42",
                      "e0ho4Cxz22fS0FGxMAfsUjYhSgd2",
                    ].includes(currentUser.uid) && (
                      <button
                        className={`${styles["pin-btn"]} ${thread.pinned ? styles["pinned"] : ""
                          }`}
                        onClick={() => handlePin(thread)}
                        title={thread.pinned ? "Unpin" : "Pin"}
                      >
                        {thread.pinned ? "Unpin Thread" : "Pin Thread"}
                      </button>
                    )}
                </div>
              </div>
            );
          })}

          {getFilteredThreads().length === 0 && (
            <div className={styles["no-results"]}>
              No threads found matching your search.
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function formatDate(date) {
  if (!date) return "";

  const now = new Date();
  const threadDate = new Date(date);
  const diffTime = Math.abs(now - threadDate);
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
    if (diffHours === 0) {
      const diffMinutes = Math.floor(diffTime / (1000 * 60));
      return `${diffMinutes}m ago`;
    }
    return `${diffHours}h ago`;
  } else if (diffDays === 1) {
    return "Yesterday";
  } else if (diffDays < 7) {
    return `${diffDays}d ago`;
  } else if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return `${weeks}w ago`;
  } else {
    return threadDate.toLocaleDateString();
  }
}

export default ThreadsPage;
